
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Boat {
    id_boat: string;
    boats_name: string;
    rgp: string;
    permissions: boolean;
    validity: string;
    length: number;
    weigth: number;
    engine_1: string;
    engine_2: string;
    engine_3: string;
    isactive?: boolean;
}

type BoatInput = Omit<Boat, 'id_boat'>

type BoatPayload = Omit<Boat, 'id_boat'>;

interface FilterInput {
    filter: string;
    value: string;
}

interface BoatsProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface BoatsContextData {
    createBoat: (boat: BoatInput) => Promise<ResponseProps>;
    updateBoat: (boat: Boat) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
    filterBoats: (filter: FilterInput) => Promise<void>;
}

interface UseBoatsOptions {
    loadBoats?: boolean;
}

interface UseBoatsData extends BoatsContextData {
    boats: Boat[];
}

const BoatsContext = createContext<BoatsContextData>(
    {} as BoatsContextData
);

const boatsQueryKey = ['boats'];
const emptyBoats: Boat[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchBoats() {
  const response = await apiV2.get('boats');
  return Array.isArray(response?.data) ? response.data : [];
}

export function BoatsProvider({ children }: BoatsProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateBoats() {
    queryClient.invalidateQueries({ queryKey: boatsQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/boats/${id}`);
    if (response.status === 200) {
      invalidateBoats();
      return {
        success: true,
        message: "Barco removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover barco."
    };
  }

  function normalizeBoatPayload(boat: Partial<Boat>, currentBoat?: Boat): BoatPayload {
    return {
      boats_name: boat.boats_name || '',
      rgp: boat.rgp ?? currentBoat?.rgp ?? '',
      permissions: Boolean(boat.permissions),
      validity: boat.validity || '',
      length: Number(boat.length),
      weigth: Number(boat.weigth),
      engine_1: boat.engine_1 || '',
      engine_2: boat.engine_2 || '',
      engine_3: boat.engine_3 || ''
    };
  }

  async function createBoat(boatInput: BoatInput) {
    const response = await apiV2.post('/boats', normalizeBoatPayload(boatInput));
    if (response.status === 201) {
      invalidateBoats();
      return {
        success: true,
        message: "Barco adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: response?.data.error || "Erro ao adicionar barco."
    };

  }

  async function filterBoats(filter: FilterInput) {
    const boats = await queryClient.fetchQuery({
      queryKey: boatsQueryKey,
      queryFn: fetchBoats,
      staleTime: 5 * 60 * 1000
    });
    const filterValue = filter.value === 'Sim' ? true : filter.value;
    queryClient.setQueryData(
      boatsQueryKey,
      boats.filter((boat: Boat) => boat[filter.filter as keyof Boat] === filterValue)
    );
  }

  async function updateBoat(boat: Boat) {
    const boats = await queryClient.fetchQuery({
      queryKey: boatsQueryKey,
      queryFn: fetchBoats,
      staleTime: 5 * 60 * 1000
    });
    const currentBoat = boats.find((currentBoat: Boat) => currentBoat.id_boat === boat.id_boat);
    const response = await apiV2.put(`/boats/${boat.id_boat}`, normalizeBoatPayload(boat, currentBoat));
    if (response.status === 204) {
      invalidateBoats();
      return {
        success: true,
        message: "Barco editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar barco."
    };
  }

  return (
    <BoatsContext.Provider value={{ createBoat, filterBoats, updateBoat, updateOnDelete }}>
      {children}
    </BoatsContext.Provider>
  );
}

export function UseBoats(options: UseBoatsOptions = {}): UseBoatsData {
  const context = useContext(BoatsContext);
  const { loadBoats = false } = options;

  const boatsQuery = useQuery({
    queryKey: boatsQueryKey,
    queryFn: fetchBoats,
    enabled: loadBoats && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    boats: boatsQuery.data || emptyBoats
  };
}
